import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    secondary: {
      main: '#9aa6c0',
    },
    background: {
      default: '#071129',
      paper: 'rgba(255,255,255,0.02)',
    },
    text: {
      primary: '#e6eef9',
      secondary: '#9aa6c0',
    },
    error: {
      main: '#f87171',
    },
    success: {
      main: '#22c55e',
    },
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
          backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.04)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent !important',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#e6eef9',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.15)',
              backgroundColor: 'transparent !important',
              transform: 'translateY(-1px)',
            },
            '&.Mui-focused': {
              borderColor: 'rgba(59,130,246,0.6)',
              backgroundColor: 'transparent !important',
              transform: 'translateY(-1px)',
              boxShadow: '0 0 0 2px rgba(59,130,246,0.1)',
            },
            // Remove validation colors - keep clean appearance
            '&.Mui-error': {
              borderColor: 'rgba(255,255,255,0.08)',
              backgroundColor: 'transparent !important',
            },
            '&.Mui-success': {
              borderColor: 'rgba(255,255,255,0.08)',
              backgroundColor: 'transparent !important',
            },
            // Force transparent background for all states
            '& input': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent !important',
            },
            '& fieldset': {
              border: 'none',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#e6eef9',
            fontWeight: 500,
            transition: 'color 0.3s ease',
            '&.Mui-focused': {
              color: '#9aa6c0',
            },
            '&.Mui-error': {
              color: '#e6eef9',
            },
            '&.Mui-success': {
              color: '#e6eef9',
            },
          },
          '& .MuiOutlinedInput-input::placeholder': {
            color: 'rgba(230,238,249,0.35)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 500,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
          boxShadow: '0 8px 22px rgba(37,99,235,0.18)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 28px rgba(37,99,235,0.25)',
            background: 'linear-gradient(135deg, #1d4ed8, #2563eb)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 6px 16px rgba(37,99,235,0.2)',
          },
        },
        outlined: {
          border: '1px solid rgba(255,255,255,0.1)',
          color: 'rgba(230,238,249,0.9)',
          '&:hover': {
            background: 'rgba(255,255,255,0.05)',
            borderColor: 'rgba(255,255,255,0.2)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        },
        icon: {
          color: '#9aa6c0',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0b2a52',
          border: '1px solid rgba(255,255,255,0.1)',
          backdropFilter: 'blur(6px)',
          '& .MuiMenuItem-root': {
            color: '#e6eef9',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(59,130,246,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(59,130,246,0.3)',
              },
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent !important',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#e6eef9',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.15)',
              backgroundColor: 'transparent !important',
              transform: 'translateY(-1px)',
            },
            '&.Mui-focused': {
              borderColor: 'rgba(59,130,246,0.6)',
              backgroundColor: 'transparent !important',
              transform: 'translateY(-1px)',
              boxShadow: '0 0 0 2px rgba(59,130,246,0.1)',
            },
            // Force transparent background for all states
            '& input': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiInputBase-input': {
              backgroundColor: 'transparent !important',
            },
            '& .MuiSelect-select': {
              padding: '12px 14px',
              '&:focus': {
                backgroundColor: 'transparent',
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: '#e6eef9',
            fontWeight: 500,
            transition: 'color 0.3s ease',
            '&.Mui-focused': {
              color: '#3b82f6',
            },
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  </React.StrictMode>
);
